import { useEffect, useState } from 'react';

import useScrollToNextField from './hooks/useScrollToNextField';

import { useEcommerceTransmissionCreateMutation } from '@/api/ecommerce';
import ChatMessage from '@/components/ChatMessage';
import { DevtoolsNotFound } from '@/components/devtools/Devtools';
import Footer from '@/components/layouts/Footer';
import Navbar from '@/components/layouts/Navbar';
import SEO from '@/components/layouts/SEO';
import Button, {
  ButtonLoading,
  ButtonRightArrow,
} from '@/components/ui/Button';
import ProgressBar from '@/components/ui/ProgressBar';
import {
  HELPFIND_PHONE_TRANSMISSION,
  PRODUCT,
  RESPONSIVE_QUERY,
} from '@/consts';
import { CONSULTANTS } from '@/features/v2/consultants';
import { CaseCardVariants } from '@/features/v2/MobileMenu';
import { scrollToTop } from '@/helpers/scrollToTop';

import { LINE_TYPES_NAME } from './components/CheckboxGroupLineTypes';
import { PLOT_TYPE_NAME } from './components/RadioGroupPlotType';
import { CO_OWNERS_COUNT_NAME } from './components/SelectCoOwnersCount';
import { ECOMMERCE_STEPS } from './const';
import {
  ECOMMERCE_DEFAULT_VALUES,
  type EcommerceFormValues,
  SCREEN_FIELDS,
} from './schema';
import Step1PlotInfo from './steps/Step1PlotInfo';
import Step2ContactDetails from './steps/Step2ContactDetails';
import { useLingui } from '@lingui/react';
import * as Sentry from '@sentry/react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

const TOTAL_SCREENS = 2;

const CHAT_MESSAGES: Record<number, string> = {
  1: 'Odpowiedz na kilka pytań o działkę. Dzięki temu dopasujemy dokument do Twojej sytuacji.',
  2: 'Podaj swoje dane kontaktowe — na ten adres e-mail wyślemy gotowy dokument po zakupie.',
};

const TransmissionCreateLeadForm = () => {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const [screen, setScreen] = useState(1);
  const createOrder = useEcommerceTransmissionCreateMutation();
  const showDevtools = import.meta.env.VITE_SHOW_DEV_TOOLS === 'true';

  const methods = useForm<EcommerceFormValues>({
    mode: 'onBlur',
    defaultValues: ECOMMERCE_DEFAULT_VALUES,
  });

  const {
    trigger,
    getValues,
    watch,
    formState: { errors },
  } = methods;
  const values = watch();

  const hasPlotInfoErrors =
    screen === 1 &&
    !!(
      errors[PLOT_TYPE_NAME] ||
      errors[LINE_TYPES_NAME] ||
      errors[CO_OWNERS_COUNT_NAME]
    );

  const isDesktop = useMediaQuery({ query: RESPONSIVE_QUERY.IS_DESKTOP });
  // line_types is a checkbox group — on mobile, scrolling away after the
  // first check overshoots and fights the user picking more than one option.
  const plotInfoGatingFields = isDesktop
    ? [LINE_TYPES_NAME, PLOT_TYPE_NAME]
    : [PLOT_TYPE_NAME];

  const { ref: nextFieldRef } = useScrollToNextField(
    values,
    plotInfoGatingFields
  );

  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const fields = SCREEN_FIELDS[screen] ?? [];
    const filled = fields.filter((field) => {
      const value = values[field];
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'boolean') return value;
      return !!value;
    }).length;
    setProgress(
      fields.length > 0 ? Math.round((filled / fields.length) * 100) : 0
    );
  }, [values, screen]);

  const handleNext = async () => {
    const fieldsToValidate = SCREEN_FIELDS[screen];
    if (fieldsToValidate) {
      const valid = await trigger(
        fieldsToValidate as (keyof EcommerceFormValues)[]
      );
      if (!valid) return;
    }

    if (screen < TOTAL_SCREENS) {
      setScreen((s) => s + 1);
      scrollToTop();
      return;
    }

    const values = getValues();
    const order = await createOrder.mutateAsync({
      firstName: values.first_name,
      lastName: values.last_name,
      phone: values.phone,
      email: values.email,
      lineTypes: values.line_types,
      plotType: values.plot_type,
      coOwnersCount: values.co_owners_count,
    });
    scrollToTop();
    navigate(`/ecommerce/transmission/${order.id}`);
  };

  const handleBack = () => {
    setScreen((s) => Math.max(s - 1, 1));
    scrollToTop();
  };

  return (
    <div className="flex min-h-screen flex-col">
      <SEO id={PRODUCT.TRANSMISSION} />

      <Navbar
        phone={HELPFIND_PHONE_TRANSMISSION}
        disableMobileMenu
        caseCardVariant={CaseCardVariants.EMPTY}
        compactPhoneButton
      />

      <div className="flex w-full flex-col pb-[100px] xxs:pb-[125px] sm:pb-[110px] lg:hidden" />

      <main className="mx-auto w-full flex-1 px-4 py-[22px] pb-[100px] sm:max-w-screen-sm md:max-w-tablet md:px-6 lg:max-w-screen lg:px-8 lg:pb-[120px] lg:pt-[30px]">
        <div className="w-full items-start lg:flex lg:gap-x-5">
          <div className="flex flex-col gap-[16px] lg:w-[670px] lg:gap-y-5">
            <ChatMessage
              variant="primary"
              className="pb-0"
              consultant={CONSULTANTS.KASIA_ASSISTANT}
              description={CHAT_MESSAGES[screen]}
              hideCloseButton
            />

            <FormProvider {...methods}>
              <form
                onSubmit={(e) => e.preventDefault()}
                className="flex flex-col gap-[22px] lg:gap-5"
              >
                {screen === 1 && <Step1PlotInfo />}
                {screen === 2 && <Step2ContactDetails />}

                <div className="flex flex-wrap-reverse items-center justify-end gap-5">
                  {screen > 1 && (
                    <Button
                      intent="secondary"
                      type="button"
                      onClick={handleBack}
                      className="w-full hover:bg-gray-400 hover:text-blue-600 active:bg-gray-400 active:text-blue-600 md:w-auto md:shrink-0"
                    >
                      {i18n.t('Wróć')}
                    </Button>
                  )}
                  <div ref={nextFieldRef} className="w-full md:max-w-max">
                    <Button
                      type="button"
                      onClick={handleNext}
                      loading={createOrder.isPending}
                      disabled={hasPlotInfoErrors}
                      className="w-full md:w-auto"
                    >
                      {screen === TOTAL_SCREENS
                        ? i18n.t('Przejdź do wyboru dokumentu')
                        : i18n.t('Przejdź dalej')}
                      <ButtonRightArrow />
                      <ButtonLoading />
                    </Button>
                  </div>
                </div>
              </form>
            </FormProvider>
          </div>

          <div>
            <div className="fixed inset-x-1/2 top-[75px] z-40 w-full -translate-x-1/2 lg:sticky lg:top-4 lg:w-fit lg:max-w-[290px] lg:-translate-x-0">
              <div className="relative w-full">
                <span className="translate-z-0 absolute left-0 top-[-12px] flex h-[41px] w-screen bg-oxford-blue-400 lg:hidden" />
                <div className="mx-auto px-2.5 sm:max-w-screen-sm sm:px-4 md:max-w-tablet md:px-6 lg:max-w-none lg:px-0">
                  <ProgressBar
                    steps={ECOMMERCE_STEPS}
                    currentStep={screen}
                    progress={progress}
                    variant="default"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {showDevtools && <DevtoolsNotFound />}
    </div>
  );
};

export default Sentry.withProfiler(TransmissionCreateLeadForm);
